"use client"

import { useCallback, useState } from "react"

import type { ToolStatusSnapshot } from "../components/chat/groupAssistantSteps"
import { extractToolErrorMessage } from "../utils/extractToolErrorMessage"

interface ToolStreamEvent {
  type?: string
  tool_name?: string
  tool_call_id?: string
  status?: string
  message?: string
  subject?: string
  wrapped_type?: string
  done?: boolean
  // UTC tz-aware ISO timestamps stamped BE-side. The @tool decorator and
  // `sub_agent.py` both emit these now (memoized per inner tcid — first-emit
  // wins across multi-chunk re-emissions), so they ride every relevant event
  // and survive into `turn_artifacts` for rehydrate. Reading them directly
  // beats stamping `Date.now()` at snapshot-apply time, which loses the real
  // start anchor on multi-chunk tool_call accumulators (the FE saw the seed
  // and the terminal as the same render).
  started_at?: string
  completed_at?: string
  // BE-stamped event-emission timestamp. Always present on the wire (set by
  // `simple_agent.py` / `decorator.py` at the moment the event is produced).
  // Used as the unified clock anchor so streaming and rehydrate compute the
  // same elapsed for tools that don't get explicit `started_at`/`completed_at`
  // (top-level @tool events emit only `timestamp`). Without this, streaming
  // would anchor to `Date.now()` at FE-arrival — which includes FE-side React
  // batching/buffering jitter that rehydrate doesn't see, making streaming
  // read longer than reload for the same tool run.
  timestamp?: string
}

function parseIsoMs(iso: string | undefined): number | undefined {
  if (!iso) return undefined
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? undefined : ms
}

interface UseToolStatusSnapshotsReturn {
  statusByCallId: Map<string, ToolStatusSnapshot>
  recordStreamEvent: (parsed: ToolStreamEvent, content?: string) => void
  resetStatusSnapshots: () => void
  markInProgressAsInterrupted: () => void
}

// Owns the per-tool-call status snapshot map that drives <ToolCallGroup />.
// One reducer-shaped entry point — pass every parsed stream event through it
// and the hook decides whether to seed, flip to terminal, or leave alone.
// Both the addin's ChatContext and the web app's ChatPage consume it; the
// snapshot map then flows into `groupAssistantSteps(messages, statusByCallId)`.
export function useToolStatusSnapshots(): UseToolStatusSnapshotsReturn {
  const [statusByCallId, setStatusByCallId] = useState<Map<string, ToolStatusSnapshot>>(
    () => new Map(),
  )

  const recordStreamEvent = useCallback((parsed: ToolStreamEvent, content?: string) => {
    // Top-level text from the master agent implicitly completes any tools
    // still in-flight — the model can't stream prose while a tool is running.
    // Wrapped text (`wrapped_type: "text"`, a sub-agent's LLM monologue) is
    // NOT a top-level flush: the outer sub_agent step is still running, so
    // its snapshot must stay in_progress.
    if (parsed.type === "text" && !parsed.wrapped_type) {
      flushInProgressToDone(setStatusByCallId)
      return
    }

    if (!parsed.tool_call_id) return

    // Sub-agent inner events arrive wrapped as `type: "tool_result"` with the
    // original wire type on `wrapped_type` and the INNER tool's tcid on
    // `tool_call_id`. Branching on the unwrapped type routes every inner
    // event (tool_call seed / tool_result chunk / tool_status terminal) to
    // the inner snapshot keyed by `parsed.tool_call_id`, instead of treating
    // them all as outer `tool_result` envelopes.
    const effectiveType = parsed.wrapped_type ?? parsed.type

    if (effectiveType === "tool_call") {
      const wireAnchor = parseIsoMs(parsed.started_at) ?? parseIsoMs(parsed.timestamp)
      seedInProgress(setStatusByCallId, parsed.tool_call_id, parsed.tool_name, wireAnchor)
      return
    }

    if (effectiveType === "tool_status") {
      applyToolStatus(setStatusByCallId, parsed)
      return
    }

    if (effectiveType === "tool_result") {
      applyToolResult(setStatusByCallId, parsed, content)
      return
    }
  }, [])

  const resetStatusSnapshots = useCallback(() => {
    setStatusByCallId(new Map())
  }, [])

  const markInProgressAsInterrupted = useCallback(() => {
    setStatusByCallId(prev => {
      let mutated = false
      const next = new Map(prev)
      const now = Date.now()
      for (const [tcid, snap] of next) {
        if (snap.status !== "in_progress") continue
        next.set(tcid, { ...snap, status: "interrupted", completedAt: now })
        mutated = true
      }
      return mutated ? next : prev
    })
  }, [])

  return { statusByCallId, recordStreamEvent, resetStatusSnapshots, markInProgressAsInterrupted }
}

type SetStatusMap = React.Dispatch<React.SetStateAction<Map<string, ToolStatusSnapshot>>>

function seedInProgress(
  setStatus: SetStatusMap,
  toolCallId: string,
  toolName: string | undefined,
  startedAtMs?: number,
): void {
  setStatus(prev => {
    if (prev.has(toolCallId)) return prev
    const next = new Map(prev)
    const startedAt = startedAtMs ?? Date.now()
    next.set(toolCallId, { status: "in_progress", startedAt, toolName })
    return next
  })
}

function applySubject(setStatus: SetStatusMap, toolCallId: string, subject: string): void {
  setStatus(prev => {
    const existing = prev.get(toolCallId)
    if (existing?.subject === subject) return prev
    const next = new Map(prev)
    next.set(toolCallId, { status: "in_progress", startedAt: Date.now(), ...existing, subject })
    return next
  })
}

// Text from the model means any tool still mid-flight has implicitly finished
// — the model can't stream prose while a tool is running. This is the only
// completion signal for tools that emit neither `tool_status` nor a `done:true`
// `tool_result` (e.g. `create_document`, which streams its result inline).
function flushInProgressToDone(setStatus: SetStatusMap): void {
  setStatus(prev => {
    let mutated = false
    const next = new Map(prev)
    const now = Date.now()
    for (const [tcid, snap] of next) {
      if (snap.status !== "in_progress") continue
      next.set(tcid, { ...snap, status: "done", completedAt: now })
      mutated = true
    }
    return mutated ? next : prev
  })
}

// Handler for `tool_result` events (after wrapper unwrapping in
// `recordStreamEvent` — wrapped tool_status events go to `applyToolStatus`
// instead). Two terminal paths land here:
// - `parsed.done === true`: sub_agent.py finally-block marker for the outer
//   step. Explicit terminal.
// - Belt-and-suspenders: a non-sub_agent tool's content carrying a
//   `tool_error(...)` envelope marks the call as errored. The decorator
//   emits both a `tool_status: error` and a `tool_result` with error JSON;
//   this is a reliable error signal even if it races ahead of tool_status.
// Streaming tool_result chunks (no done flag, no error envelope) are a
// no-op — real completion comes via the matching `tool_status` event.
function applyToolResult(
  setStatus: SetStatusMap,
  parsed: ToolStreamEvent,
  content: string | undefined,
): void {
  const toolCallId = parsed.tool_call_id
  if (!toolCallId) return

  let terminalStatus: ToolStatusSnapshot["status"] | undefined
  let errorMessage: string | undefined

  if (parsed.done === true) {
    terminalStatus = "done"
  } else if (parsed.tool_name !== "sub_agent" && content) {
    errorMessage = extractToolErrorMessage(content)
    if (errorMessage) terminalStatus = "error"
  }

  if (!terminalStatus) return

  const isExplicit = parsed.done === true
  const finalStatus = terminalStatus
  const finalErrorMessage = errorMessage

  const wireStartedAt = parseIsoMs(parsed.started_at)
  const wireCompletedAt = parseIsoMs(parsed.completed_at)
  const wireTimestamp = parseIsoMs(parsed.timestamp)

  setStatus(prev => {
    const existing = prev.get(toolCallId)
    // Don't downgrade an explicit error → done from belt-and-suspenders.
    if (existing?.status === "error" && finalStatus !== "error") return prev
    // Belt-and-suspenders path: only flip if still in_progress.
    if (!isExplicit && existing && existing.status !== "in_progress") return prev
    const next = new Map(prev)
    const startedAt = existing?.startedAt ?? wireStartedAt ?? wireTimestamp ?? Date.now()
    const completedAt = wireCompletedAt ?? wireTimestamp ?? Date.now()
    next.set(toolCallId, {
      status: finalStatus,
      startedAt,
      completedAt,
      errorMessage: finalErrorMessage,
      toolName: existing?.toolName ?? parsed.tool_name,
      subject: existing?.subject,
    })
    return next
  })
}

// Per-tool terminal signal. "done" / "error" are emitted by the @tool
// decorator's wrapper; "interrupted" is emitted per-tcid by simple_agent.py's
// stop handler for every pending tool_call. All three drive the dot in
// ToolCallGroup. "processing" is ignored — it can carry progress text but
// doesn't change the dot.
function applyToolStatus(setStatus: SetStatusMap, parsed: ToolStreamEvent): void {
  const toolCallId = parsed.tool_call_id
  if (!toolCallId) return

  const nextStatus = parsed.status
  if (nextStatus === "running" && parsed.subject) {
    applySubject(setStatus, toolCallId, parsed.subject)
    return
  }
  if (nextStatus !== "done" && nextStatus !== "error" && nextStatus !== "interrupted") return

  const wireStartedAt = parseIsoMs(parsed.started_at)
  const wireCompletedAt = parseIsoMs(parsed.completed_at)
  const wireTimestamp = parseIsoMs(parsed.timestamp)

  setStatus(prev => {
    const next = new Map(prev)
    const existing = next.get(toolCallId)
    next.set(toolCallId, {
      status: nextStatus,
      startedAt: existing?.startedAt ?? wireStartedAt ?? wireTimestamp ?? Date.now(),
      completedAt: wireCompletedAt ?? wireTimestamp ?? Date.now(),
      errorMessage: nextStatus === "error" ? parsed.message : undefined,
      toolName: existing?.toolName,
      subject: existing?.subject,
    })
    return next
  })
}
